import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms'; // Import Reactive Forms
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { StreamActivityService } from 'src/app/services/stream-activity.service';

@Component({
    selector: 'app-record-activity-session',
    templateUrl: './record-activity-session.component.html',
    styleUrl: './record-activity-session.component.scss',
    standalone: false
})
export class RecordActivitySessionComponent implements OnInit, OnDestroy {
  @Input() projectId!: number;
  @Input() caseId!: number;
  @Input() activityId!: number;

  @Output() startedRecording = new EventEmitter<boolean>();
  @Output() uploadComplete = new EventEmitter<void>();

  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;
  @ViewChild('voiceBaseModal') voiceBaseModal!: TemplateRef<any>;
  @ViewChild('startRecordingModal') startRecordingModal!: TemplateRef<any>;

  // --- Device & Form ---
  setupForm!: FormGroup;
  videoDevices: MediaDeviceInfo[] = [];
  audioInputDevices: MediaDeviceInfo[] = [];
  audioOutputDevices: MediaDeviceInfo[] = [];
  speakers = [{ name: 'Single Speaker', value: 'y' }, { name: 'Multiple Speakers', value: 'n' }];
  
  // --- State Management ---
  isRecording = false;
  showVideoUploading = false;
  loading = false;
  allowStartRecording = false;
  muteMic = true;
  testSpeakers = false;
  
  // --- Recording & Upload ---
  private videoStream!: MediaStream | undefined;
  private mediaRecorder!: MediaRecorder;
  private passConditionObservable!: Subscription;
  private audioMeterInterval: any;
  
  uploadInProgress = true;
  progressValue = 0;
  recordingStartTime = 0;
  time = '00:00:00';
  intervalId: any; 

  startRecordingModalRef: any;
  continueModalRef: any;

  constructor (private authService: AuthService, public streamService: StreamActivityService, private modalService: NgbModal, private fb: FormBuilder) {}

  ngOnInit() {
    this.streamService.baseline.base_voice_start = null;
    this.streamService.baseline.base_voice_end = null;
    
    // --- Initialize Reactive Form ---
    this.setupForm = this.fb.group({
      speakerSelected: [null, Validators.required],
      selectedCamera: [null, Validators.required],
      selectedMicrophone: [null, Validators.required],
      selectedSpeaker: [null, Validators.required]
    });

    this.passConditionObservable = this.streamService.passConditions$.subscribe(pass => {
      this.allowStartRecording = pass;
    });

    this.getMediaDevices();
    navigator.mediaDevices.ondevicechange = () => this.getMediaDevices();
  }

  ngOnDestroy(): void {
    this.stopRecording(); // Ensure all streams are stopped
    this.passConditionObservable?.unsubscribe();
    clearInterval(this.audioMeterInterval);
  }

  get f() { return this.setupForm.controls; }

  getMediaDevices() {
    navigator.mediaDevices.getUserMedia({ audio: true, video: true })
    .then(stream => {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        this.videoDevices = devices.filter(device => device.kind === 'videoinput');
        this.audioInputDevices = devices.filter(device => device.kind === 'audioinput');
        this.audioOutputDevices = devices.filter(device => device.kind === 'audiooutput');
        
        // --- Auto-select first device and update form ---
        if (this.videoDevices.length > 0 && !this.f['selectedCamera'].value) {
          this.f['selectedCamera'].setValue(this.videoDevices[0].deviceId);
          this.onCameraChange(); // Trigger video stream
        }
        if (this.audioInputDevices.length > 0 && !this.f['selectedMicrophone'].value) {
          this.f['selectedMicrophone'].setValue(this.audioInputDevices[0].deviceId);
          this.onMicrophoneChange(); // Trigger mic stream
        }
        if (this.audioOutputDevices.length > 0 && !this.f['selectedSpeaker'].value) {
          this.f['selectedSpeaker'].setValue(this.audioOutputDevices[0].deviceId);
        }
      });
      stream.getTracks().forEach(track => track.stop());
    })
    .catch(error => console.error('Error accessing media devices:', error));
  }

  async onCameraChange() {
    const deviceId = this.f['selectedCamera'].value;
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
    }
    
    if (deviceId) {
      navigator.mediaDevices.getUserMedia({ video: { deviceId: deviceId } })
        .then(async (stream) => {
          this.videoStream = stream;
          this.streamService.selectedCamera = deviceId;
          this.videoPlayer.nativeElement.srcObject = this.videoStream;
          this.checkFrameRate();
          await this.streamService.checkConditions(this.videoPlayer.nativeElement);
        })
        .catch(error => console.error('Error accessing camera:', error));
    }
  }

  onMicrophoneChange() {
    const deviceId = this.f['selectedMicrophone'].value;
    clearInterval(this.audioMeterInterval);

    if (deviceId) {
      navigator.mediaDevices.getUserMedia({ audio: { deviceId: deviceId } })
      .then(stream => {
        this.streamService.selectedMicrophone = deviceId;
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        microphone.connect(analyser);

        this.audioMeterInterval = setInterval(() => {
          const bufferLength = analyser.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          analyser.getByteFrequencyData(dataArray);
          this.updateAudioMeter(dataArray);
        }, 50);
      })
      .catch(error => console.error('Error accessing microphone:', error));
    }
  }

  start() {
    this.setupForm.markAllAsTouched();
    if (this.setupForm.invalid || !this.allowStartRecording) {
      if (!this.allowStartRecording) {
        this.authService.toastErrorMessage('Please ensure your face is detected and lighting is good.', 'Conditions Not Met');
      }
      return;
    }
    
    this.startRecordingModalRef = this.modalService.open(this.startRecordingModal, { size: 'lg', centered: true, backdrop: 'static', keyboard: false });
  }

  continueAfterGuidelines() {
    this.startRecordingModalRef.close();
    
    const speakerSelected = this.f['speakerSelected'].value;
    
    if (speakerSelected === 'n') {
      this.continueModalRef = this.modalService.open(this.voiceBaseModal, { size: 'lg', centered: true, backdrop: 'static', keyboard: false });
    } else {
      this.startRecording();
    }
  }

  async startRecording() {
    const formValue = this.setupForm.value;
    this.streamService.project_id = this.projectId;
    this.streamService.case_id = this.caseId;
    this.streamService.activity_id = this.activityId;
    
    await this.streamService.updateActivityStatus(this.projectId, this.caseId, this.activityId, formValue.speakerSelected); // TODO
    this.continueModalRef?.close();
    this.startedRecording.emit(true);

    this.streamService.uploadProgressObs.subscribe((data) => {
      if (data.finishUploading) {
        this.uploadInProgress = false;
        setTimeout(() => this.uploadComplete.emit(), 3000);
      }
      this.progressValue = data.progress;
    });

    navigator.mediaDevices.getUserMedia({
      video: { deviceId: formValue.selectedCamera },
      audio: { deviceId: formValue.selectedMicrophone }
    })
    .then(async (stream) => {
      if (!stream) {
        this.authService.toastErrorMessage('Video stream not available.', 'Error!');
        return;
      }
      
      this.isRecording = true;
      this.streamService.isRecording = true;
      this.videoStream = stream;
      
      this.videoPlayer.nativeElement.srcObject = this.videoStream;
      
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm; codecs=vp9',
				audioBitsPerSecond: 128000,
				videoBitsPerSecond : 4500000
      });

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          this.streamService.storeChunk(event.data);
        }
      };

      this.mediaRecorder.onstart = () => {
        this.recordingStartTime = this.videoPlayer.nativeElement.currentTime;
        this.streamService.baseline.baseline_start = 0;
        this.streamService.baseline.baseline_end = 30;
      };

      this.mediaRecorder.start(10000);
      
      let totalSeconds = 0;
      this.intervalId = setInterval(() => {
        totalSeconds++;
        this.time = this.formatTime(totalSeconds);
      }, 1000);
      
      this.streamService.processUploadQueue(); //TODO
      this.streamService.checkConditions(this.videoPlayer.nativeElement);
    })
    .catch(error => console.error('Error accessing media:', error));
  }

  async stopRecording() {
    clearInterval(this.intervalId);

    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }

    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
    }
    
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      const videoElement = this.videoPlayer.nativeElement;
      const endTime = videoElement.currentTime;
      this.streamService.videoLength = Math.floor((endTime - this.recordingStartTime) / 60);
      this.videoPlayer.nativeElement.srcObject = null;
    }

    this.streamService.stopStreaming();
    this.isRecording = false;
    this.streamService.isRecording = false;
    this.showVideoUploading = true;
    this.startedRecording.emit(false);
  }


  markTime(mark: string) {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      const videoElement = this.videoPlayer.nativeElement;
      const currentTime = videoElement.currentTime;
      const elapsedRecordingTime = Math.floor(currentTime - this.recordingStartTime);

      switch (mark) {
        case 'v_start':
          this.streamService.baseline.base_voice_start = elapsedRecordingTime;
          break;
        case 'v_end':
          this.streamService.baseline.base_voice_end = elapsedRecordingTime;
          break;
        case 'reset_v':
          this.streamService.baseline.base_voice_start = null;
          this.streamService.baseline.base_voice_end = null;
          break;
      }
    }
  }

  formatTime(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds].map(v => v < 10 ? '0' + v : v).join(':');
  }

  isMarked(mark: string) {
		switch (mark) {
			case 'v_start':
				return this.streamService.baseline.base_voice_start ? true : false;
			case 'v_end':
				return this.streamService.baseline.base_voice_end ? true : false;
		}

		return false;
	}

  updateAudioMeter(dataArray: Uint8Array) {
    const allPids = document.querySelectorAll('.pid');
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    const numPidsToColor = Math.round(average / 10); // Adjust this for sensitivity

    allPids.forEach((pid, index) => {
      if (index < numPidsToColor) {
        pid.classList.add('bg-green');
      } else {
        pid.classList.remove('bg-green');
      }
    });
  }

  checkFrameRate() {
		if (this.videoPlayer.nativeElement.srcObject instanceof MediaStream) {
			const videoTracks = this.videoPlayer.nativeElement.srcObject.getVideoTracks();
	
			if (videoTracks && videoTracks.length > 0) { // Check if video tracks exist
				const videoTrack = videoTracks[0];
				const settings = videoTrack.getSettings();
				const actualFrameRate = settings.frameRate; // Safe to access now
				
				if (actualFrameRate !== undefined) { // Check if frameRate is defined
					this.streamService.frameRate = actualFrameRate;
					const minFrameRate = 25;
					this.allowStartRecording = actualFrameRate >= minFrameRate;
		
					if (actualFrameRate < minFrameRate) {
						console.error('Camera does not meet minimum frame rate requirement.');
						// You can display a more user-friendly error message here
					}
				} else {
					console.warn('Frame rate is undefined.');
					// You might want to handle this case specifically
				}
			} else {
				console.warn('No video tracks found.');
				// You can display a message to the user indicating no video is available
			}
		} else {
			console.warn('Video source is not a MediaStream, cannot check frame rate.');
		}
	}

  playsound() {
		if (this.f['selectedSpeaker'].value) {
			if (this.testSpeakers) {
				this.audioPlayer.nativeElement.pause();
				this.testSpeakers = false;
			} else {
				this.testSpeakers = true;
				this.audioPlayer.nativeElement.currentTime = 0;
				this.audioPlayer.nativeElement.src = "./../../../assets/sound_check.mp3";
				this.audioPlayer.nativeElement.setSinkId(this.f['selectedSpeaker'].value)
					.then(() => this.audioPlayer.nativeElement.play())
					.catch(error => console.error('Error setting audio output:', error));
			}
		}
  }

  muteStatusChange() {
    this.muteMic = !this.muteMic;
		this.audioPlayer.nativeElement.pause(); // Stop audio if mic is muted
		this.testSpeakers = false;
  }
}
